/**
 * /app/$tenantSlug/payroll/tax-declaration/queue — HR / payroll verification
 * queue for investment declarations.
 *
 * RBAC: company_admin / hr_manager / accountant (mirrors the backend
 * RequireRole on /api/v1/payroll/declarations/*). The role-gate is the UX
 * mirror; the backend is the boundary.
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import { useDeclarationQueue } from "@/hooks/use-tax-declaration";
import { useDirectory } from "@/hooks/use-directory";
import type { DeclarationStatus } from "@/types/tax-declaration";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { formatPaise } from "@/lib/expense-format";
import { fyLabel } from "@/lib/indian-fy";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/tax-declaration/queue",
)({
  component: DeclarationQueueRoute,
});

const STATUSES: DeclarationStatus[] = [
  "draft",
  "declared",
  "proofs_open",
  "under_review",
  "finalised",
];

// Human labels for the raw status enum tokens in the filter dropdown.
const STATUS_LABELS: Record<DeclarationStatus, string> = {
  draft: "Draft",
  declared: "Declared",
  proofs_open: "Proofs Open",
  under_review: "Under Review",
  finalised: "Finalised",
};

function currentFYStartYear(): number {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
}

function DeclarationQueueRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const [status, setStatus] = useState<DeclarationStatus | "">("under_review");
  const [fy] = useState(currentFYStartYear());

  if (!isAccountingAdmin(user?.role)) {
    return (
      <PayrollModuleShell
        title="Tax Declarations"
        description="Verification queue."
        gate={false}
      >
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            You do not have access to the tax-declaration verification queue.
          </CardContent>
        </Card>
      </PayrollModuleShell>
    );
  }

  const { data, isLoading } = useDeclarationQueue({
    fy,
    status: status === "" ? undefined : status,
  });
  // Resolve employee ids → names (incl. deactivated employees), like the
  // detail page. An unknown id falls back to "User #<id>".
  const dir = useDirectory({ includeInactive: true });

  function declaredTotal(items: { declared_paise: number }[]): number {
    return items.reduce((s, i) => s + i.declared_paise, 0);
  }
  function verifiedTotal(items: { verified_paise?: number | null }[]): number {
    return items.reduce((s, i) => s + (i.verified_paise ?? 0), 0);
  }
  function pendingProofs(items: { item_status: string }[]): number {
    return items.filter(
      (i) => i.item_status === "pending" || i.item_status === "submitted",
    ).length;
  }

  return (
    <PayrollModuleShell
      title="Tax Declarations"
      description="Verify employee investment declarations and finalise to drive the TDS true-up."
      gate={false}
    >
      <div className="mb-4 flex items-center gap-3">
        <Label htmlFor="status-filter" className="text-sm font-medium">
          Status
        </Label>
        <select
          id="status-filter"
          className="h-9 pointer-coarse:h-11 rounded-md border border-input bg-background px-3 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as DeclarationStatus | "")}
        >
          <option value="">All</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {data && (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>FY</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Declared</TableHead>
                  <TableHead className="text-right">Verified</TableHead>
                  <TableHead className="text-right">Proofs Pending</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No declarations match this filter.
                    </TableCell>
                  </TableRow>
                )}
                {data.items.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{dir.label(d.employee_id)}</TableCell>
                    <TableCell>{fyLabel(d.financial_year)}</TableCell>
                    <TableCell>
                      <StatusBadge status={d.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPaise(declaredTotal(d.items))}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPaise(verifiedTotal(d.items))}
                    </TableCell>
                    <TableCell className="text-right">{pendingProofs(d.items)}</TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="outline">
                        <Link
                          to="/app/$tenantSlug/payroll/tax-declaration/$declId"
                          params={{ tenantSlug, declId: String(d.id) }}
                        >
                          Review
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </PayrollModuleShell>
  );
}
